// Shared helper for posting lead-capture events to GoHighLevel (GHL) inbound
// webhooks. There are TWO separate endpoints on purpose (per Tracy) — each
// event drives its own GHL workflow / text notification to home advisors,
// so they shouldn't share one URL.
//
// Suggested location: src/utils/ghlWebhooks.js (sibling to your other
// utils/helpers — adjust the import path in HomePortalPage.js if your
// project structure differs).
//
// TODO: paste your real GHL inbound webhook URLs below. In GHL:
//   Automation → Workflows → (your workflow) → Add Trigger → "Inbound Webhook"
//   → copy the generated Webhook URL.
// Until you do, calls are safely skipped (logged to console, nothing sent).

const GHL_WEBHOOK_HOME_CLAIMED = "https://services.leadconnectorhq.com/hooks/bZI6dyyFAn2ZSD9YUdpT/webhook-trigger/403dabfd-cb5a-477e-8d06-a4ac63ae7ba6";
const GHL_WEBHOOK_HEALTH_CHECK_COMPLETED = "https://services.leadconnectorhq.com/hooks/bZI6dyyFAn2ZSD9YUdpT/webhook-trigger/7091f9a5-6099-4828-a86e-e05259da24ab";

// Fire-and-forget POST helper. Never throws — a GHL/webhook outage should
// never block someone from moving forward in the funnel. Returns true/false
// only so a caller *could* show a subtle indicator; nothing should gate on it.
async function postToGhl(url, payload) {
  if (!url || url.includes("REPLACE_WITH")) {
    console.warn("[ghlWebhooks] Skipped — webhook URL is still a placeholder. Payload was:", payload);
    return false;
  }
  try {
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return resp.ok;
  } catch (err) {
    console.error("[ghlWebhooks] POST failed:", err);
    return false;
  }
}

// Fired from HomePortalPage.js the instant a visitor confirms (or
// self-reports) their identity on the "Is this you?" step — BEFORE they've
// necessarily created an account. This lets home advisors follow up on
// partial leads who bail before finishing signup.
export function sendHomeClaimedWebhook({
  firstName,
  lastName,
  ownerMatch,        // true = matched the public-record owner name we showed them, false = self-reported / edited
  matchedOwnerName,  // the public-record name we displayed, if any (useful for QA / spotting mismatches)
  address,
  city,
  state,
  zipCode,
  yearBuilt,
  squareFeet,
  bedrooms,
  bathrooms,
}) {
  return postToGhl(GHL_WEBHOOK_HOME_CLAIMED, {
    event: "home_claimed",
    firstName,
    lastName,
    ownerMatch,
    matchedOwnerName: matchedOwnerName || null,
    address,
    city,
    state,
    zipCode,
    yearBuilt,
    squareFeet,
    bedrooms,
    bathrooms,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    submittedAt: new Date().toISOString(),
  });
}

// Call this from HomeBuddha.js when the Home Health Check-Up quiz is
// submitted/completed. Pass along whatever signals selling intent (e.g. a
// "thinking about selling?" quiz question) so GHL can route hot leads to
// advisors — this is the "who might be selling" signal Tracy wants.
//
// Not wired in yet — HomeBuddha.js / the quiz-submit handler wasn't part of
// this file set. Import this function there and call it where the quiz
// result is finalized, e.g.:
//
//   import { sendHealthCheckCompletedWebhook } from "../utils/ghlWebhooks";
//   ...
//   sendHealthCheckCompletedWebhook({
//     firstName, lastName, address, city, state, zipCode,
//     healthScore: score,
//     sellingInterest: answers.sellingInterest, // whatever field name your quiz uses
//     answers,
//   });
export function sendHealthCheckCompletedWebhook({
  firstName,
  lastName,
  address,
  city,
  state,
  zipCode,
  healthScore,
  sellingInterest,   // e.g. "yes" / "maybe" / "no" / "within 12 months" — however your quiz captures it
  answers,           // full answer set, optional — include if you want raw data visible in GHL
}) {
  return postToGhl(GHL_WEBHOOK_HEALTH_CHECK_COMPLETED, {
    event: "health_check_completed",
    firstName,
    lastName,
    address,
    city,
    state,
    zipCode,
    healthScore,
    sellingInterest: sellingInterest || null,
    answers: answers || null,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    submittedAt: new Date().toISOString(),
  });
}